import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, Navigation, Ban, Check, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { CAUSES } from "@/lib/causes";
import { NGOCard } from "@/components/NGOCard";
import { Button } from "@/components/ui/button";

function Counter({ value, label, highlight }) {
  return (
    <div className={`px-4 py-2 ${highlight ? "" : ""}`} data-testid={`trust-counter`}>
      <div className={`text-2xl md:text-3xl font-extrabold ${highlight ? "text-gold" : "text-white"}`}>{value}</div>
      <div className="text-xs md:text-sm text-stone-300 mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ ngos: "—", field_verified: "—", districts: "—" });
  const [featured, setFeatured] = useState([]);
  const [stories, setStories] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/orgs?tier=3").then((r) => setFeatured((r.data.orgs || []).slice(0, 3))).catch(() => {});
    api.get("/stories").then((r) => setStories((r.data.stories || []).slice(0, 3))).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/discover?search=${encodeURIComponent(query)}`);
  };

  const nearMe = () => {
    if (!navigator.geolocation) return navigate("/discover");
    navigator.geolocation.getCurrentPosition(
      (pos) => navigate(`/discover?near_lat=${pos.coords.latitude}&near_lng=${pos.coords.longitude}&radius=50`),
      () => navigate("/discover")
    );
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/40867ab1-76cf-4596-88b0-eac4328872b7/images/c3c0c156d0fe87885c3a7018ff0ded1f4b4b3be2678b3bad45b938824aa7eff6.jpeg" alt="Volunteers restoring a village lake in Tamil Nadu" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-brand/95 via-slate-brand/85 to-slate-brand/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              {t("hero.title")}
            </h1>
            {lang === "en" && (
              <p className="font-tamil text-lg sm:text-xl text-gold-light mt-3">{t("hero.title_ta")}</p>
            )}
            <p className="text-stone-200 text-base md:text-lg mt-5 measure">{t("hero.sub")}</p>

            <form onSubmit={doSearch} className="mt-7 flex flex-col sm:flex-row gap-2 bg-white rounded-xl p-2 shadow-xl">
              <div className="flex items-center flex-1 gap-2 px-2">
                <Search size={18} className="text-stone-400 shrink-0" />
                <input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("hero.search_ph")}
                  className="w-full py-2.5 text-sm text-stone-800 outline-none bg-transparent"
                />
              </div>
              <Button type="submit" className="bg-terracotta hover:bg-terracotta-hover" data-testid="hero-search-btn">{t("common.search")}</Button>
            </form>

            <div className="flex flex-wrap gap-3 mt-5">
              <Button onClick={nearMe} className="bg-white text-slate-brand hover:bg-stone-100" data-testid="hero-nearme-btn">
                <Navigation size={16} className="mr-1.5" /> {t("hero.cta1")}
              </Button>
              <Button asChild variant="outline" className="border-white/40 text-white bg-transparent hover:bg-white/10" data-testid="hero-csr-btn">
                <Link to="/for-csr">{t("hero.cta2")} <ArrowRight size={16} className="ml-1.5" /></Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 -mt-8 relative z-10">
        <div className="bg-slate-brand rounded-2xl shadow-xl grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-slate-700/60 py-4">
          <Counter value={`${stats.ngos}+`} label={t("trust.ngos")} />
          <Counter value={`${stats.field_verified}+`} label={t("trust.field")} />
          <Counter value={stats.districts} label={t("trust.districts")} />
          <Counter value="₹0" label={t("trust.fees")} highlight />
        </div>
      </section>

      {/* LIVE IMPACT */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-14">
        <div className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8">
          <div className="flex items-center gap-2 text-gold font-semibold text-sm"><TrendingUp size={16} /> Live impact across India</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-5">
            {[
              [Number(stats.beneficiaries || 0).toLocaleString("en-IN"), "Lives touched by listed work"],
              [Number(stats.contacts || 0).toLocaleString("en-IN"), "Direct donor–NGO connections made"],
              [stats.states || "—", "States covered and growing"],
              ["₹0", "Ever routed through this platform"],
            ].map(([n, l], i) => (
              <div key={i}>
                <div className={`text-2xl md:text-3xl font-extrabold ${i === 3 ? "text-gold" : "text-slate-brand"}`}>{n}</div>
                <div className="text-xs text-stone-500 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-brand mb-10">{t("how.title")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: t("how.give_t"), b: t("how.give_b"), to: "/discover", cta: t("nav.discover") },
            { t: t("how.csr_t"), b: t("how.csr_b"), to: "/for-csr", cta: t("nav.forCsr") },
            { t: t("how.ngo_t"), b: t("how.ngo_b"), to: "/for-ngos", cta: t("nav.forNgos") },
          ].map((col, i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-200 p-6 flex flex-col">
              <div className="w-10 h-1.5 rounded-full bg-terracotta mb-4" />
              <h3 className="text-xl font-bold text-stone-900 mb-3">{col.t}</h3>
              <p className="text-stone-600 text-sm measure flex-1">{col.b}</p>
              <Link to={col.to} className="inline-flex items-center gap-1 text-terracotta font-semibold text-sm mt-4 hover:gap-2 transition-[gap]">{col.cta} <ArrowRight size={15} /></Link>
            </div>
          ))}
        </div>
      </section>

      {/* SCALE — the size of what is unconnected */}
      <section className="bg-slate-brand text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold">The size of what is unconnected</h2>
          <p className="text-slate-300 mt-2 measure">India built a rail for payments. Giving still runs on phone calls. Not one of these numbers is a generosity problem — they are a discovery problem.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {[["₹34,908 cr", "Corporate CSR spent in a single year"], ["27,188", "Companies legally required to spend it"], ["72,233", "Projects funded, mostly through personal networks"], ["₹2.61 lakh cr", "Cumulative CSR across the last decade"]].map(([n, l], i) => (
              <div key={i} className="border-t-2 border-gold/40 pt-3">
                <div className="text-2xl md:text-3xl font-extrabold text-gold">{n}</div>
                <div className="text-sm text-slate-300 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LANGUAGE BAND */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-brand">Giving has a word in every Indian language.</h2>
        <p className="text-stone-600 mt-2 measure">The NGO secretary in a Namakkal village does not work in English. Every screen, every notice, every consent form — in the language the person actually reads.</p>
        <div className="flex flex-wrap gap-3 mt-8">
          {[["दान", "Hindi"], ["நன்கொடை", "Tamil"], ["দান", "Bengali"], ["దానం", "Telugu"], ["ದಾನ", "Kannada"], ["દાન", "Gujarati"], ["ദാനം", "Malayalam"], ["ਦਾਨ", "Punjabi"], ["ଦାନ", "Odia"]].map(([w, l], i) => (
            <div key={i} className="rounded-xl border border-stone-200 bg-white px-4 py-3 min-w-[110px]">
              <div className="text-xl text-slate-brand font-semibold">{w}</div>
              <div className="text-xs text-stone-500 mt-0.5">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* NO MONEY BANNER */}
      <section className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="rounded-2xl bg-[#081326] text-slate-100 p-8 md:p-12 text-center relative overflow-hidden">
          <div className="inline-flex items-center gap-2 text-gold font-semibold text-sm mb-3">
            <Ban size={16} /> No wallet · No escrow · No processing fee
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">{t("money.title")}</h2>
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">{t("money.body")}</p>
        </div>
      </section>

      {/* FEATURED STORIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-brand">{t("stories.title")}</h2>
            <p className="text-stone-600 mt-1 text-sm">{t("stories.sub")}</p>
          </div>
          <Link to="/stories" className="hidden sm:inline-flex items-center gap-1 text-terracotta font-semibold text-sm">{t("nav.stories")} <ArrowRight size={15} /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {stories.map((s) => (
            <Link key={s.id} to={`/stories/${s.slug}`} data-testid={`story-card-${s.slug}`} className="group bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-video overflow-hidden">
                <img src={s.hero_image} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4">
                <span className="text-xs font-semibold uppercase tracking-wide text-terracotta">{s.district}</span>
                <h3 className="text-lg font-bold text-stone-900 mt-1 leading-snug">{s.title}</h3>
                <p className="text-sm text-stone-600 mt-1.5 line-clamp-2">{s.standfirst}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BROWSE BY CAUSE */}
      <section className="bg-stone-100/60 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-brand">{t("cause.title")}</h2>
          <p className="text-stone-600 mt-1 text-sm mb-8">{t("cause.sub")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {CAUSES.map((c) => (
              <Link key={c.id} to={`/discover?cause=${c.id}`} data-testid={`cause-tile-${c.id}`}
                className="group flex flex-col items-center text-center gap-2 bg-white rounded-xl border border-stone-200 p-4 hover:border-terracotta hover:-translate-y-0.5 transition-[transform,border-color]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-terracotta-light text-terracotta group-hover:bg-terracotta group-hover:text-white transition-colors">
                  <c.Icon size={20} />
                </span>
                <span className="text-xs font-medium text-stone-700 leading-tight">{lang === "ta" ? c.name_ta : c.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED NGOs */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-brand mb-8">Field-verified organisations</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((o) => <NGOCard key={o.id} org={o} />)}
          </div>
        </section>
      )}

      {/* PHASES — how a national registry gets built */}
      <section className="bg-stone-100/60 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-brand">How a national registry gets built</h2>
          <p className="text-stone-600 mt-1 text-sm mb-8">Depth first, then breadth. One shared, open data standard the whole way.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { k: "Now", t: "The Kongu belt", b: "Erode, Tiruppur, Coimbatore, Salem, Namakkal — field-verified, every one." },
              { k: "Next", t: "Tamil Nadu", b: "All 38 districts, with district administrations as verification partners." },
              { k: "Then", t: "South India", b: "Five states, five languages, one shared data standard." },
              { k: "Goal", t: "All of India", b: "Every registered NGO, every district, free to search forever." },
            ].map((p, i) => (
              <div key={i} className="bg-white rounded-xl border border-stone-200 p-5">
                <span className="text-xs font-bold uppercase tracking-wide text-gold">{p.k}</span>
                <h3 className="font-bold text-slate-brand mt-1">{p.t}</h3>
                <p className="text-sm text-stone-600 mt-1.5">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
        <div className="rounded-2xl border-2 border-dashed border-terracotta-border bg-terracotta-light p-8 md:p-10 text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-brand max-w-2xl mx-auto">{t("closing.title")}</h2>
          <p className="text-stone-600 mt-2">{t("closing.body")}</p>
          <Button asChild className="mt-5 bg-terracotta hover:bg-terracotta-hover" data-testid="suggest-ngo-btn">
            <Link to="/for-ngos">{t("closing.cta")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
